/**
 * PoYo text-to-image catalog for Image Studio UI (ESM).
 * Keep in sync with /shared/poyo-txt2img-models.js
 */

export const DEFAULT_ASPECT_RATIO = "9:16";

export const POYO_ASPECT_RATIOS = [
  { id: "1:1", label: "1:1", hint: "Square" },
  { id: "16:9", label: "16:9", hint: "Landscape" },
  { id: "9:16", label: "9:16", hint: "Portrait" },
  { id: "4:3", label: "4:3", hint: "Standard" },
  { id: "3:4", label: "3:4", hint: "Tall" },
];

export const POYO_TXT2IMG_MODELS = [
  {
    id: "nano-banana-2-new",
    name: "Nano Banana 2",
    description: "Latest Gemini Flash Image — strong prompt following, up to 2K/4K.",
    brand: "google",
    tier: "best",
  },
  {
    id: "nano-banana-2-lite",
    name: "Nano Banana 2 Lite",
    description: "Fastest Gemini image model — drafts and high throughput.",
    brand: "google",
    tier: "budget",
  },
  {
    id: "nano-banana-2",
    name: "Nano Banana Pro",
    description: "Gemini image generation/editing family — production quality.",
    brand: "google",
    tier: "alt",
  },
  {
    id: "nano-banana",
    name: "Nano Banana",
    description: "Gemini 2.5 Flash image — quick generations.",
    brand: "google",
    tier: "budget",
  },
  {
    id: "seedream-5.0-pro",
    name: "Seedream 5.0 Pro",
    description: "ByteDance flagship — precise prompts, layout, and typography.",
    brand: "bytedance",
    tier: "best",
  },
  {
    id: "seedream-5.0-lite",
    name: "Seedream 5.0 Lite",
    description: "Faster Seedream 5 — strong quality at lower cost.",
    brand: "bytedance",
    tier: "alt",
  },
  {
    id: "seedream-4.5",
    name: "Seedream 4.5",
    description: "Advanced Seedream with text-to-image and multi-ref.",
    brand: "bytedance",
    tier: "alt",
  },
  {
    id: "seedream-4",
    name: "Seedream 4",
    description: "Solid Seedream text-to-image baseline.",
    brand: "bytedance",
    tier: "budget",
  },
  {
    id: "flux-2-pro",
    name: "Flux 2 Pro",
    description: "BFL 32B — photorealism and excellent text rendering.",
    brand: "flux",
    tier: "best",
  },
  {
    id: "flux-2-flex",
    name: "Flux 2 Flex",
    description: "Adjustable speed/quality balance for flexible workflows.",
    brand: "flux",
    tier: "alt",
  },
  {
    id: "flux-dev",
    name: "Flux Dev",
    description: "Flux Dev text-to-image — high detail creative work.",
    brand: "flux",
    tier: "alt",
  },
  {
    id: "flux-schnell",
    name: "Flux Schnell",
    description: "Fast Flux text-to-image for rapid ideation.",
    brand: "flux",
    tier: "budget",
  },
  {
    id: "grok-imagine-image",
    name: "Grok Imagine",
    description: "xAI Grok Imagine — creative, high-impact stills.",
    brand: "xai",
    tier: "best",
  },
  {
    id: "grok-imagine-image-quality",
    name: "Grok Imagine Quality",
    description: "Higher-fidelity Grok Imagine image generation.",
    brand: "xai",
    tier: "alt",
  },
  {
    id: "gpt-image-2",
    name: "GPT Image 2",
    description: "OpenAI GPT Image 2 — strong composition and editing lineage.",
    brand: "openai",
    tier: "best",
  },
  {
    id: "gpt-image-1.5",
    name: "GPT Image 1.5",
    description: "OpenAI GPT Image 1.5 — reliable production quality.",
    brand: "openai",
    tier: "alt",
  },
  {
    id: "gpt-4o-image",
    name: "GPT-4o Image",
    description: "GPT-4o image generation — versatile general-purpose stills.",
    brand: "openai",
    tier: "alt",
  },
  {
    id: "kling-o3",
    name: "Kling O3 Image",
    description: "Expressive scene composition — creative and narrative stills.",
    brand: "kling",
    tier: "alt",
  },
  {
    id: "kling-o1",
    name: "Kling O1 Image",
    description: "Character/product consistency with fine detail control.",
    brand: "kling",
    tier: "alt",
  },
  {
    id: "wan-2.7-image-pro",
    name: "Wan 2.7 Image Pro",
    description: "Alibaba high-quality text-to-image with flexible sizing.",
    brand: "wan",
    tier: "alt",
  },
  {
    id: "wan-2.7-image",
    name: "Wan 2.7 Image",
    description: "Alibaba unified text-to-image with custom sizes.",
    brand: "wan",
    tier: "budget",
  },
  {
    id: "z-image",
    name: "Z-Image",
    description: "High-quality unified text-to-image generation.",
    brand: "z",
    tier: "alt",
  },
];

export const DEFAULT_POYO_TXT2IMG_MODEL = "nano-banana-2-new";

export function autoNameFromPrompt(prompt, fallbackPrefix = "Image") {
  const cleaned = String(prompt || "")
    .replace(/[#*_`>"']/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) {
    const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
    return `${fallbackPrefix} ${stamp}`;
  }
  const words = cleaned.split(" ").filter(Boolean).slice(0, 7);
  let name = words.join(" ");
  if (name.length > 56) name = `${name.slice(0, 53).trim()}…`;
  return name.charAt(0).toUpperCase() + name.slice(1);
}
